import type { ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface PendingFieldOverlayProps {
  readonly isPending: boolean;
  readonly children: ReactNode;
  /** Override wrapper class — defaults to `inline-flex`. */
  readonly className?: string;
}

/**
 * Tight wrapper that dims a header cell + drops a tiny Loader2 spinner in the
 * top-right while a mutation for that field is in flight. Header writes hit
 * Dexie first and then wait on SSE fan-out before the rendered value changes
 * (~500 ms on prod tenants), so without an acknowledgement the cell looks
 * frozen for the round-trip. The overlay is the acknowledgement.
 *
 * `pointer-events-none` on the spinner so it doesn't intercept the cell's own
 * click target; the underlying cell keeps its hit area intact.
 */
export function PendingFieldOverlay({
  isPending,
  children,
  className,
}: PendingFieldOverlayProps) {
  return (
    <div
      className={cn(
        "relative inline-flex items-center transition-opacity",
        isPending && "opacity-70",
        className,
      )}
      data-pending={isPending || undefined}
      aria-busy={isPending || undefined}
    >
      {children}
      {isPending && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute -top-1 -right-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-blue-200"
        >
          <Loader2 className="h-3 w-3 animate-spin text-blue-500" />
        </span>
      )}
    </div>
  );
}
